import React, { createContext, FunctionComponent, useContext, useMemo } from 'react';
import config from '@app/config';

import { useJsApiLoader } from '@react-google-maps/api';

interface ConditionalGoogleMapsProviderProps {
  children: React.ReactNode;
}

export type GoogleMapsAvailability = {
  isLoaded: boolean;
  loadError?: Error;
};

const GoogleMapsAvailabilityContext = createContext<GoogleMapsAvailability>({ isLoaded: false });

export function useGoogleMapsAvailability(): GoogleMapsAvailability {
  return useContext(GoogleMapsAvailabilityContext);
}

const ConditionalGoogleMapsProvider: FunctionComponent<ConditionalGoogleMapsProviderProps> = ({ children }) => {
  // Only load Google Maps script for supplierRegistration mode
  const shouldLoadMaps = config.mode === 'supplierRegistration' && Boolean(config.googleMapsApiKey);

  // Load the script in the background. We intentionally do NOT gate rendering on it:
  // in some countries/networks (e.g. China) Maps can be blocked.
  const { isLoaded, loadError } = useJsApiLoader(
    shouldLoadMaps
      ? {
          id: 'google-maps-script',
          googleMapsApiKey: config.googleMapsApiKey,
          libraries: ['places'],
        }
      : {
          id: 'google-maps-script',
          googleMapsApiKey: '',
        },
  );

  const value = useMemo<GoogleMapsAvailability>(
    () => ({
      isLoaded: shouldLoadMaps ? isLoaded : false,
      loadError: shouldLoadMaps ? (loadError as Error | undefined) : undefined,
    }),
    [isLoaded, loadError, shouldLoadMaps],
  );

  return <GoogleMapsAvailabilityContext.Provider value={value}>{children}</GoogleMapsAvailabilityContext.Provider>;
};

export default ConditionalGoogleMapsProvider;
